import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { listTemplateVersions, revertTemplateVersion, getPromptTemplate } from '../services/api';

interface TemplateVersionHistoryProps {
  open: boolean;
  templateId: string;
  onClose: (reverted: boolean) => void;
}

interface Version {
  version_id: string;
  version_number: number;
  content: string;
  description: string;
  change_type: 'create' | 'update';
  created_at: string;
  created_by: string;
}

const changeTypeColor = (changeType: string): 'default' | 'primary' => (changeType === 'update' ? 'primary' : 'default');

const TemplateVersionHistory: React.FC<TemplateVersionHistoryProps> = ({ open, templateId, onClose }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggleExpanded = (versionNumber: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(versionNumber)) {
        next.delete(versionNumber);
      } else {
        next.add(versionNumber);
      }
      return next;
    });
  };

  useEffect(() => {
    if (open && templateId) {
      loadVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const loadVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [versionsData, templateData] = await Promise.all([
        listTemplateVersions(templateId),
        getPromptTemplate(templateId),
      ]);
      setVersions(versionsData.versions || []);
      setCurrentVersion(templateData?.template?.current_version ?? null);
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (version: Version) => {
    if (
      !window.confirm(
        `Make version ${version.version_number} the active content?\n\nThis does not create a new version - the template will simply point back at v${version.version_number}. Attribute mappings will also be restored to match that version, if it captured them. Full history is kept either way.`
      )
    ) {
      return;
    }

    setReverting(version.version_number);
    setError(null);
    try {
      await revertTemplateVersion(templateId, version.version_number);
      onClose(true);
    } catch (err: any) {
      setError(err.message || 'Failed to revert template');
    } finally {
      setReverting(null);
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  const truncate = (text: string, max: number) => (text.length > max ? text.substring(0, max) + '...' : text);

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>Version History</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No version history yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Content Preview</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => {
                  const isCurrent = v.version_number === currentVersion;
                  const isExpanded = expanded.has(v.version_number);
                  return (
                    <React.Fragment key={v.version_id}>
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip label={`v${v.version_number}`} size="small" />
                            {isCurrent && <Chip label="current" size="small" color="success" variant="outlined" />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={v.change_type} size="small" color={changeTypeColor(v.change_type)} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}
                            >
                              {isExpanded ? v.content : truncate(v.content, 80)}
                            </Typography>
                            {v.content.length > 80 && (
                              <IconButton size="small" onClick={() => toggleExpanded(v.version_number)}>
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(v.created_at)}</TableCell>
                        <TableCell>{v.created_by}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            disabled={isCurrent || reverting !== null}
                            onClick={() => handleRevert(v)}
                          >
                            {reverting === v.version_number ? <CircularProgress size={16} /> : 'Revert to this'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  bgcolor: 'grey.50',
                                  p: 2,
                                  my: 1,
                                  maxHeight: 400,
                                  overflow: 'auto',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}
                                >
                                  {v.content}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateVersionHistory;
